/**
 * widgets/registry.dashboard.tsx — Dashboard home widgets (2.1 unified registry).
 * Stejný kontrakt jako module widgets: WidgetSpec + WidgetShell + scope.
 * Stats system.* žijí v registry.system; tady dashboard-specific list/stat karty.
 */
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import i18n from '../lib/i18n';
import {
  Activity,
  Box,
  CheckCircle2,
  Network,
  Package,
  Server,
  Users,
  XCircle,
} from 'lucide-react';
import { api } from '../lib/api';
import { Badge, StatCard, StatusDot } from '../components/ui';
import { MeshNodesGlanceWidget } from '../components/mesh/MeshNodesGlance';
import type { WidgetRenderProps, WidgetSpec } from './types';

// ── Shared data hooks (dedupe via queryKey) ──────────────────────────────────

function useMeshNodesList() {
  return useQuery({
    queryKey: ['mesh-nodes'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        nodes: Array<{
          node_id: string;
          hostname: string;
          status: string;
          ip_private: string;
          env?: string;
          acm_version?: string;
          last_seen?: string;
        }>;
      }>('mesh.nodes', { action: 'list' }),
    refetchInterval: 30_000,
  });
}

function useAcmServices() {
  return useQuery({
    queryKey: ['acm-services'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        services: Array<{
          id: string;
          label: string;
          port: number;
          public_enabled: boolean;
          public_domain: string | null;
        }>;
      }>('acm-self.services_state', {}),
  });
}

function useMeshTenants() {
  return useQuery({
    queryKey: ['mesh-tenants'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        tenants: Array<{
          id: string;
          slug: string;
          name: string;
          plan: string;
          enabled: boolean;
          node_count: number;
        }>;
      }>('mesh.tenants', { action: 'list' }),
    refetchInterval: 30_000,
  });
}

function useModules() {
  return useQuery({
    queryKey: ['dash-modules'],
    queryFn: () =>
      api.mcp<{ modules: Array<{ name: string; maturity: string; core: boolean }> }>(
        'acm-self.modules_list',
      ),
    staleTime: 60_000,
  });
}

function useContainers() {
  return useQuery({
    queryKey: ['containers'],
    queryFn: async () => {
      const r = await api.mcp<{ containers: Array<{ name: string; state: string; image: string }> }>(
        'docker.container_list',
        {},
      ).catch(() => ({ containers: [] as Array<{ name: string; state: string; image: string }> }));
      return r.containers ?? [];
    },
    refetchInterval: 15_000,
  });
}

function useHealth() {
  return useQuery({
    queryKey: ['dash-health'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        status: string;
        tests: Array<{ name: string; status: string; message: string }>;
      }>('toolbox.health_status', {}).catch(() => ({
        ok: false,
        status: 'unknown',
        tests: [] as Array<{ name: string; status: string; message: string }>,
      })),
    refetchInterval: 60_000,
  });
}

function useTasks() {
  return useQuery({
    queryKey: ['dash-tasks'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        tasks: Array<{ name: string; enabled: boolean; last_run_status: string }>;
      }>('tasks.task_list', {}).catch(() => ({ ok: false, tasks: [] as Array<{ name: string; enabled: boolean; last_run_status: string }> })),
    staleTime: 60_000,
  });
}

function useAudit() {
  return useQuery({
    queryKey: ['dash-audit'],
    queryFn: () =>
      api.mcp<{
        entries: Array<{ category: string; event: string; ts: string }>;
      }>('acm-self.audit_log', { limit: 8 }).catch(() => ({ entries: [] as Array<{ category: string; event: string; ts: string }> })),
    staleTime: 30_000,
  });
}

function timeAgo(ts: string): string {
  const d = Date.now() - new Date(ts).getTime();
  const m = Math.floor(d / 60000);
  if (m < 1) return i18n.t('dashboard.timeAgoNow', 'teď');
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function isFreshOnline(status: string, lastSeen?: string, maxAgeMs = 180_000): boolean {
  if (status === 'offline') return false;
  if (!lastSeen) return false;
  const age = Date.now() - new Date(lastSeen).getTime();
  return Number.isFinite(age) && age <= maxAgeMs;
}

// ── Components ──────────────────────────────────────────────────────────────

/** Jednotný kvalitní mesh widget (IP, ACM/commit, role, latency) — sdílený s panel/topology. */
function MeshNodesWidget({ format, viewport }: WidgetRenderProps) {
  return <MeshNodesGlanceWidget format={format} viewport={viewport} />;
}

function AcmServicesWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data: svcResp } = useAcmServices();
  const acmServices = svcResp?.services ?? [];
  return (
    <div className='divide-y divide-gray-800/60'>
      {acmServices.map((svc) => (
        <div key={svc.id} className='flex items-center gap-2 px-1 py-2'>
          <StatusDot status={svc.public_enabled ? 'running' : 'stopped'} />
          <span className='text-xs text-white flex-1'>{svc.label}</span>
          {svc.public_enabled && svc.public_domain
            ? (
              <a
                href={`https://${svc.public_domain}`}
                target='_blank'
                rel='noopener'
                className='text-[10px] font-mono text-emerald-400 truncate max-w-[200px]'
              >
                {svc.public_domain}
              </a>
            )
            : (
              <span className='text-[10px] text-gray-600'>
                {t('dashboard.internalService', 'interní')}
              </span>
            )}
        </div>
      ))}
    </div>
  );
}

function TenantsWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useMeshTenants();
  const tenants = data?.tenants ?? [];
  return (
    <div className='space-y-1.5 max-h-[280px] overflow-y-auto'>
      {tenants.slice(0, 12).map((tn) => (
        <div
          key={tn.id}
          className='flex items-center gap-2 px-2 py-1.5 rounded-lg border border-gray-800/60'
        >
          <Users size={12} className='text-gray-500' />
          <span className='text-xs text-white flex-1 truncate'>{tn.name}</span>
          <Badge variant='gray'>{tn.plan}</Badge>
          <span className='text-[10px] text-gray-600'>{tn.node_count}</span>
        </div>
      ))}
      {tenants.length === 0 && (
        <p className='text-xs text-gray-600 py-4 text-center'>
          {t('dashboard.noTenants', 'Žádní tenanti')}
        </p>
      )}
    </div>
  );
}

function HealthChecksWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useHealth();
  const tests = data?.tests ?? [];
  return (
    <div className='space-y-1 max-h-[280px] overflow-y-auto'>
      {tests.slice(0, 12).map((test, i) => (
        <div key={i} className='flex items-center gap-2 text-xs py-1'>
          <StatusDot
            status={test.status === 'pass' || test.status === 'ok' ? 'running' : 'error'}
          />
          <span className='text-gray-200 flex-1 truncate'>{test.name}</span>
          <span className='text-[10px] text-gray-600 truncate max-w-[40%]'>{test.message}</span>
        </div>
      ))}
      {tests.length === 0 && (
        <p className='text-xs text-gray-600 py-4 text-center'>
          {t('dashboard.noHealth', 'Žádné testy')}
        </p>
      )}
    </div>
  );
}

function CoreContainersWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data: containers = [] } = useContainers();
  const core = containers.filter((c) =>
    /acm|postgres|redis|emqx|npm|keycloak/i.test(c.name)
  );
  return (
    <div className='space-y-1 max-h-[280px] overflow-y-auto'>
      {(core.length ? core : containers).slice(0, 12).map((c) => (
        <div key={c.name} className='flex items-center gap-2 text-xs py-1'>
          <StatusDot status={c.state === 'running' ? 'running' : 'stopped'} />
          <span className='text-gray-200 flex-1 font-mono truncate'>{c.name}</span>
          <span className='text-[10px] text-gray-600'>{c.state}</span>
        </div>
      ))}
      {containers.length === 0 && (
        <p className='text-xs text-gray-600 py-4 text-center'>
          {t('dashboard.noContainers', 'Žádné kontejnery')}
        </p>
      )}
    </div>
  );
}

function TasksWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useTasks();
  const tasks = data?.tasks ?? [];
  return (
    <div className='space-y-1 max-h-[280px] overflow-y-auto'>
      {tasks.slice(0, 10).map((task, i) => (
        <div key={i} className='flex items-center gap-2 text-xs py-1'>
          <StatusDot
            status={task.last_run_status === 'ok'
              ? 'running'
              : task.last_run_status === 'error'
              ? 'error'
              : 'pending'}
          />
          <span className='text-gray-200 flex-1 truncate'>{task.name}</span>
          {!task.enabled && <Badge variant='gray'>off</Badge>}
        </div>
      ))}
      {tasks.length === 0 && (
        <p className='text-xs text-gray-600 py-4 text-center'>
          {t('dashboard.noTasks', 'Žádné úlohy')}
        </p>
      )}
    </div>
  );
}

function AuditWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useAudit();
  const audit = data?.entries ?? [];
  return (
    <div className='divide-y divide-gray-800/60 max-h-[280px] overflow-y-auto'>
      {audit.slice(0, 8).map((a, i) => (
        <div key={i} className='flex items-center gap-2 py-2'>
          <span className='text-[10px] text-gray-600 w-8'>{timeAgo(a.ts)}</span>
          <Badge variant='secondary'>{a.category}</Badge>
          <span className='text-xs text-gray-400 flex-1 truncate'>{a.event}</span>
        </div>
      ))}
      {audit.length === 0 && (
        <p className='text-xs text-gray-600 py-4 text-center'>
          {t('dashboard.noActivity', 'Žádná aktivita')}
        </p>
      )}
    </div>
  );
}

function StatTenantsWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useMeshTenants();
  const n = data?.tenants?.length ?? 0;
  return (
    <StatCard label={t('dashboard.statTenants', 'Tenanti')} value={n} icon={Users} />
  );
}

function StatMeshWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useMeshNodesList();
  const nodes = data?.nodes ?? [];
  const online = nodes.filter((n) => isFreshOnline(n.status, n.last_seen)).length;
  return (
    <StatCard
      label={t('dashboard.statMesh', 'Mesh nody')}
      value={nodes.length}
      icon={Network}
      sub={t('dashboard.statMeshSub', '{{count}} online', { count: online })}
    />
  );
}

function StatModulesWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useModules();
  const n = data?.modules?.length ?? 0;
  return (
    <StatCard label={t('dashboard.statModules', 'Moduly')} value={n} icon={Package} />
  );
}

function StatContainersWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data: containers = [] } = useContainers();
  const running = containers.filter((c) => c.state === 'running').length;
  return (
    <StatCard
      label={t('dashboard.statContainers', 'Kontejnery')}
      value={containers.length}
      icon={Box}
      sub={t('dashboard.statContainersSub', '{{count}} běží', { count: running })}
    />
  );
}

function StatHealthWidget(_props: WidgetRenderProps) {
  const { t } = useTranslation();
  const { data } = useHealth();
  const ok = data?.ok;
  return (
    <StatCard
      label={t('dashboard.statHealth', 'Health')}
      value={ok ? t('dashboard.healthOk', 'OK') : (data?.status ?? '?')}
      icon={ok ? CheckCircle2 : XCircle}
      variant={ok ? 'success' : 'error'}
    />
  );
}

// ── Registry ────────────────────────────────────────────────────────────────

export const DASHBOARD_WIDGETS: WidgetSpec[] = [
  {
    id: 'dashboard.mesh-nodes',
    title: 'Mesh nodes',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Network,
    since: '2.1.0',
    component: MeshNodesWidget,
    // alias: mesh.nodes (stejná komponenta MeshNodesGlanceWidget)
  },
  {
    id: 'dashboard.acm-services',
    title: 'Public services',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Server,
    since: '2.1.0',
    component: AcmServicesWidget,
  },
  {
    id: 'dashboard.tenants',
    title: 'Tenants',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Users,
    since: '2.1.0',
    component: TenantsWidget,
  },
  {
    id: 'dashboard.health-checks',
    title: 'Health checks',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Activity,
    since: '2.1.0',
    component: HealthChecksWidget,
  },
  {
    id: 'dashboard.core-containers',
    title: 'Core containers',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Box,
    since: '2.1.0',
    component: CoreContainersWidget,
  },
  {
    id: 'dashboard.tasks',
    title: 'Scheduled tasks',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Activity,
    since: '2.1.0',
    component: TasksWidget,
  },
  {
    id: 'dashboard.audit',
    title: 'Recent activity',
    kind: 'custom',
    format: 'md',
    formats: ['sm', 'md', 'lg'],
    icon: Activity,
    since: '2.1.0',
    component: AuditWidget,
  },
  {
    id: 'dashboard.stat-tenants',
    title: 'Tenants — count',
    kind: 'stat',
    format: 'sm',
    formats: ['sm', 'md'],
    icon: Users,
    since: '2.1.0',
    component: StatTenantsWidget,
  },
  {
    id: 'dashboard.stat-mesh',
    title: 'Mesh nodes — count',
    kind: 'stat',
    format: 'sm',
    formats: ['sm', 'md'],
    icon: Network,
    since: '2.1.0',
    component: StatMeshWidget,
  },
  {
    id: 'dashboard.stat-modules',
    title: 'Modules — count',
    kind: 'stat',
    format: 'sm',
    formats: ['sm', 'md'],
    icon: Package,
    since: '2.1.0',
    component: StatModulesWidget,
  },
  {
    id: 'dashboard.stat-containers',
    title: 'Containers — count',
    kind: 'stat',
    format: 'sm',
    formats: ['sm', 'md'],
    icon: Box,
    since: '2.1.0',
    component: StatContainersWidget,
  },
  {
    id: 'dashboard.stat-health',
    title: 'Health — status',
    kind: 'stat',
    format: 'sm',
    formats: ['sm', 'md'],
    icon: Activity,
    since: '2.1.0',
    component: StatHealthWidget,
  },
];

/** Mapování starého dashboard layout LS → kanonická registry ID. */
export const DASHBOARD_LEGACY_ID_MAP: Record<string, string> = {
  'stat-cpu': 'system-settings.cpu',
  'stat-ram': 'system-settings.ram',
  'stat-disk': 'system-settings.disk',
  'stat-uptime': 'system-settings.uptime',
  'stat-tenants': 'dashboard.stat-tenants',
  'stat-mesh': 'dashboard.stat-mesh',
  'stat-modules': 'dashboard.stat-modules',
  'stat-containers': 'dashboard.stat-containers',
  'stat-health': 'dashboard.stat-health',
  'mesh-nodes': 'dashboard.mesh-nodes',
  'acm-services': 'dashboard.acm-services',
  tenants: 'dashboard.tenants',
  'health-checks': 'dashboard.health-checks',
  'core-containers': 'dashboard.core-containers',
  tasks: 'dashboard.tasks',
  audit: 'dashboard.audit',
};

/** Výchozí home dashboard layout (kanonická ID z REGISTRY). */
export const DASHBOARD_DEFAULT_LAYOUT = [
  { widgetId: 'system-settings.cpu', format: 'sm' as const },
  { widgetId: 'system-settings.ram', format: 'sm' as const },
  { widgetId: 'system-settings.disk', format: 'sm' as const },
  { widgetId: 'system-settings.uptime', format: 'sm' as const },
  { widgetId: 'dashboard.stat-tenants', format: 'sm' as const },
  { widgetId: 'dashboard.stat-mesh', format: 'sm' as const },
  { widgetId: 'dashboard.stat-modules', format: 'sm' as const },
  { widgetId: 'dashboard.stat-containers', format: 'sm' as const },
  { widgetId: 'dashboard.stat-health', format: 'sm' as const },
  { widgetId: 'dashboard.mesh-nodes', format: 'md' as const },
  { widgetId: 'dashboard.acm-services', format: 'md' as const },
  { widgetId: 'dashboard.tenants', format: 'lg' as const },
  { widgetId: 'dashboard.health-checks', format: 'md' as const },
  { widgetId: 'dashboard.core-containers', format: 'md' as const },
  { widgetId: 'dashboard.tasks', format: 'md' as const },
  { widgetId: 'dashboard.audit', format: 'md' as const },
];
